import { SegmentedControl, SegmentedControlItem } from '@mantine/core';
import classnames from 'classnames';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { useUnmount } from 'react-use';
import classes from './Segment.module.css';
import { Wrapper } from './Segment.style';

export type SegmentProps = {
  value: string;
  data: string[] | SegmentedControlItem[];
  onChange: (value: string) => void;
  onHover?: (id: number | undefined) => void;
  className?: string;
  activeSegments?: SlotId[];
  maxWidth?: number;
};

export const Segment = forwardRef<HTMLDivElement, SegmentProps>((props, forwardedRef) => {
  const { data, value, activeSegments = [], maxWidth } = props;
  const [index, setIndex] = useState<number | undefined>(undefined);
  const hoverTimeoutRef = useRef<any>();

  const combinedClassName = classnames(
    'Segment-wrapper',
    activeSegments.map((i) => 'active-' + i),
    props.className
  );

  function onMouseOver(ev: any) {
    clearTimeout(hoverTimeoutRef.current);
    const target = ev.target;
    const targetClassName = target.className;
    if (!targetClassName.includes('mantine-SegmentedControl-label')) return;

    const innerText = target.innerText;
    const idx = parseInt(innerText) - 1;

    if (!activeSegments.includes(String(idx) as any)) {
      setIndex(undefined);
      return;
    }

    setIndex(idx);
  }

  function onMouseOut() {
    clearTimeout(hoverTimeoutRef.current);
    if (typeof index === 'undefined') return;

    hoverTimeoutRef.current = setTimeout(() => {
      setIndex(undefined);
    }, 200);
  }

  useUnmount(() => {
    clearTimeout(hoverTimeoutRef.current);
  });

  useEffect(() => {
    if (props.onHover) {
      props.onHover(index);
    }
  }, [index]);

  const style: React.CSSProperties = {};
  if (maxWidth) {
    style.maxWidth = `${maxWidth}px`;
  }

  return (
    <Wrapper
      ref={forwardedRef}
      style={style}
      className={combinedClassName}
      data-testid='Segment-wrapper'
    >
      <SegmentedControl
        value={value}
        radius='xl'
        size='xs'
        data={data}
        classNames={classes}
        onChange={props.onChange}
        onMouseOver={onMouseOver}
        onMouseOut={onMouseOut}
        fullWidth
      />
    </Wrapper>
  );
});

Segment.displayName = 'Segment';

export default Segment;
